import { useTranslation } from 'react-i18next';
import React from 'react';
import { Form, InputRef, Modal } from 'antd';
import { useAppDispatch, useAppSelector } from 'store/hook';
import { setIsOpenAddNoteModal, setNotes } from 'store/slices/dataNotes';
import api from 'api';
import classes from './NotesTab.module.scss';
import { IDocument } from 'interfaces';
import { setDataDocumentTable } from 'store/slices/dataDocumentsSlice';
import TextArea from 'antd/es/input/TextArea';
import dayjs from 'dayjs'
import { getTheme } from 'helpers/getTheme';
import cn from 'classnames';

type AddNoteModalProps = {
  isOpen: boolean;
  onCancel: () => void;
  documentId: string;
  documentName: string;
};

export default function AddNoteModal ({ isOpen, onCancel, documentId, documentName }: AddNoteModalProps) {
  const [form] = Form.useForm();
  
  const [isLaoding, setIsLoading] = React.useState<boolean>(false);
  const [noteText, setNoteText] = React.useState<string>('');

  const textAreaRef = React.useRef<InputRef | null>(null);
  
  const { notes } = useAppSelector((state) => state.notes);
  const { userData } = useAppSelector((state) => state.userData);
  const { dataDocumentTable } = useAppSelector(state => state.documents);
  const { dataRoom } = useAppSelector((state) => state.dataRoom);
  const { messageApi } = useAppSelector((state) => state.windowState);

  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  React.useEffect(() => textAreaRef.current?.focus(), []);
  
  const addNote = async (values: { text: string }) => {
    setIsLoading(true);
    try {
      const timestamp = new Date().toLocaleString();
      const offset = new Date().getTimezoneOffset() * 60 * 1000;
      
      const {
        data: { id },
      } = await api.createNotes({
        ...values,
        document_id: documentId,
        room_id: dataRoom?.id!,
        details: { note_text: values.text }
      });
      
      notes &&
        dispatch(setNotes([
          ...notes,
          {
            timestamp,
            timestamp_in_seconds: dayjs().valueOf() + offset,
            author: `${userData?.first_name} ${userData?.last_name}`,
            text: values.text,
            can_share: true,
            id: id,
            visible_to: []
          },
        ]));
      const changedHasNotes = dataDocumentTable?.find((el) => el.id === documentId) as IDocument;
      if(changedHasNotes?.has_notes) return;

      const changedData = dataDocumentTable?.map((el) => el.id === documentId ? {...el, has_notes: true} : el);
      dispatch(setDataDocumentTable(changedData));
      messageApi?.success(`${t('Documents.success.notesAdd')} ${documentName}`);
    } catch (err) {
      console.log(err, 'creating note error');
      messageApi?.error(`${t('Documents.error.notesAdd')} ${documentName}`);
    } finally {
      setIsLoading(false);
      dispatch(setIsOpenAddNoteModal(false));
      setNoteText('');
      form.setFieldValue('text', '')
    }
  };

  const onChangeNoteText = (event: React.ChangeEvent<HTMLTextAreaElement>) => setNoteText(event.target.value);

  return (
    <Modal
      width={'500px'}
      open={isOpen}
      onCancel={onCancel}
      centered
      onOk={form.submit}
      okText={t('Documents.management.add')}
      cancelButtonProps={{style: {display: 'none'}}}
      loading={isLaoding}
      okButtonProps={{
        disabled: !noteText || !noteText.trim(),
        style: {width: '100%', margin: 0},
      }}
      title={t('Documents.modals.notes.title')}
      className={cn(classes.modalDetails, getTheme())}
    >
      <Form
        form={form}
        onFinish={addNote}
        layout='vertical'
        className={classes.addInput}
      >
        <Form.Item 
          rules={[
            { required: true },
          ]}
          label={t('Documents.modals.notes.label')}
          name='text'
          className={classes.input}
        >
          <TextArea
            ref={textAreaRef}
            disabled={isLaoding}
            className={classes.textarea}
            value={noteText}
            onChange={onChangeNoteText}
            autoSize={{minRows: 4, maxRows: 4}}
            count={{
              show: true,
              max: 500,
              exceedFormatter: (txt, { max }) => txt.split('').slice(0, max).join(''),
            }}
            placeholder={t('Documents.modals.notes.placholder')}
            allowClear
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};
